
import { Component, Vue } from "vue-property-decorator";
import AddressDisplay from "@/components/AddressDisplay.vue";
import {
  actions,
  CalendarAttendee,
  CalendarEntry,
  Contact,
  Facility,
  PersonForMeeting
} from "@/survey";
import { getContactById, getFacilityById, getPeopleFromStore } from "@/store";
import ContactDisplayNameEmailCall from "@/components/ContactDisplayNameEmailCall.vue";
import { dateFormatter } from "@/components/formatters";
import PdfGenerator from "@/export/pdf";
import { generateSpreadsheetSingleTableAndDownload } from "@/export/excel";

@Component({
  components: {
    ContactDisplayNameEmailCall,
    AddressDisplay
  },
  watch: {
    filter() {
      this.$data.currentPage = 1;
    },
    perPage() {
      this.$data.currentPage = 1;
    }
  }
})
export default class EventList extends Vue {
  apiLocation = Vue.prototype.$apiLocation;
  client = Vue.prototype.$surveyClient;

  deleteEvent(id: number) {
    if (confirm("Are you sure you want to delete this event?")) {
      this.$store.dispatch(actions.DELETE_CALENDAR_ENTRY, {
        id,
        success: () => this.$router.push("/events")
      });
    }
  }

  data() {
    return {
      filter: "",
      perPage: 25,
      perPageOptions: [10, 25, 50, 100],
      currentPage: 1,
      sortBy: "start",
      sortDesc: false,
      bulkMode: false,
      selectedEventIds: [] as number[],
      bulkDeleting: false,
      bulkError: ""
    };
  }

  getSubject(event: CalendarEntry): string {
    if (event.facility === undefined || event.facility === null) {
      return "";
    }
    const facility = getFacilityById(event.facility);
    if (facility === undefined) {
      return "";
    }
    return (
      "Meeting at " +
      facility.name +
      (event.start !== undefined && event.start !== null ? ` - ${this.formatDate(event.start)}` : "")
    );
  }

  getContactForFacility(value: number | undefined | null): Contact | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }
    const facility = getFacilityById(value);
    if (
      facility === undefined ||
      facility.contact === null ||
      facility.contact === undefined
    ) {
      return undefined;
    }
    return this.getContactForId(facility.contact);
  }

  getFacilityForId(value: number | undefined | null): Facility | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }
    return getFacilityById(value);
  }

  getContactForId(value: number | undefined | null): Contact | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }
    return getContactById(value);
  }

  getAttendee(attendee: CalendarAttendee): PersonForMeeting {
    let foundPerson: PersonForMeeting | null = null;
    getPeopleFromStore().forEach((person: PersonForMeeting) => {
      if (person.type === "contact" && attendee.contact === person.id) {
        foundPerson = person;
      }
      if (person.type === "user" && attendee.user === person.id) {
        foundPerson = person;
      }
    });
    if (foundPerson === null) {
      return {
        name: "Unknown attendee",
        email: "",
        company: "",
        type: attendee.contact ? "contact" : "user",
        id: attendee.contact || attendee.user || 0
      };
    }
    return foundPerson;
  }

  get fields() {
    const fields: any[] = [
      { key: "toggle_details", label: "", sortable: false, thStyle: { width: "36px" } },
      { key: "start", sortable: true, formatter: (value: number | Date | null | undefined) => this.formatDate(value) },
      { key: "end", sortable: true, formatter: (value: number | Date | null | undefined) => this.formatDate(value) },
      {
        key: "facility",
        sortable: true,
        formatter: (value: number | undefined | null) => this.facilityName(value)
      },
      {
        key: "facility_contact",
        label: "Facility Contact",
        sortable: true,
        formatter: (_value: unknown, _key: string, item: CalendarEntry) => this.contactName(this.getContactForFacility(item.facility))
      },
      { key: "subject", sortable: true }
    ];
    if (this.$data.bulkMode && this.canBulkDelete) {
      fields.unshift({ key: "bulk_select", label: "", sortable: false, thStyle: { width: "42px" } });
    }
    return fields;
  }

  get rowCount() {
    return this.filteredEvents.length;
  }

  get canExport() {
    const user = this.$store.state.authentication.currentUser;
    return user !== null && (user.admin || user.export);
  }

  get canBulkDelete() {
    const user = this.$store.state.authentication.currentUser;
    return user !== null && user.admin;
  }

  get filteredEvents() {
    const globalFilter = this.normalize(this.$data.filter);
    return this.events.filter((event: CalendarEntry) => {
      return !globalFilter || this.searchableText(event).includes(globalFilter);
    });
  }

  get sortedFilteredEvents() {
    const sortBy = this.$data.sortBy;
    const sortDesc = this.$data.sortDesc;
    return this.filteredEvents.slice().sort((first: CalendarEntry, second: CalendarEntry) => {
      const result = this.valueForSort(first, sortBy).localeCompare(
        this.valueForSort(second, sortBy),
        undefined,
        { numeric: true, sensitivity: "base" }
      );
      return sortDesc ? -result : result;
    });
  }

  get sortLabel() {
    const field = this.fields.find((candidate: any) => candidate.key === this.$data.sortBy);
    const direction = this.$data.sortDesc ? "descending" : "ascending";
    return `${field ? field.label || field.key : "Start"} (${direction})`;
  }

  get events() {
    return this.$store.state.calendar || [];
  }

  attendeesFor(event: CalendarEntry) {
    return event.attendees || [];
  }

  clearFilters() {
    this.$data.filter = "";
    this.$data.currentPage = 1;
  }

  toggleBulkMode() {
    this.$data.bulkMode = !this.$data.bulkMode;
    this.$data.bulkError = "";
    if (!this.$data.bulkMode) {
      this.clearBulkSelection();
    }
  }

  isEventSelected(event: CalendarEntry) {
    return event.id !== undefined && event.id !== null && this.$data.selectedEventIds.includes(event.id);
  }

  setEventSelected(event: CalendarEntry, selected: boolean) {
    if (event.id === undefined || event.id === null) {
      return;
    }
    const selectedEventIds = this.$data.selectedEventIds as number[];
    if (selected && !selectedEventIds.includes(event.id)) {
      selectedEventIds.push(event.id);
    }
    if (!selected) {
      this.$data.selectedEventIds = selectedEventIds.filter(id => id !== event.id);
    }
  }

  selectFilteredEvents() {
    const ids = this.filteredEvents
      .map((event: CalendarEntry) => event.id)
      .filter((id: number | null | undefined): id is number => id !== undefined && id !== null);
    this.$data.selectedEventIds = Array.from(new Set([...(this.$data.selectedEventIds as number[]), ...ids]));
  }

  clearBulkSelection() {
    this.$data.selectedEventIds = [];
  }

  deleteSelectedEvents() {
    const selectedEventIds = (this.$data.selectedEventIds as number[]).slice();
    if (selectedEventIds.length === 0) {
      return;
    }
    if (!confirm(`Delete ${selectedEventIds.length} selected event${selectedEventIds.length === 1 ? "" : "s"}? This cannot be undone.`)) {
      return;
    }
    const user = this.$store.state.authentication.currentUser;
    if (user === null || !user.admin) {
      this.$data.bulkError = "Only administrators can bulk delete events.";
      return;
    }
    this.$data.bulkDeleting = true;
    this.$data.bulkError = "";
    this.client.bulkDeleteCalendarEntries(selectedEventIds)
      .then(() => this.$store.dispatch(actions.REFRESH_USER_DATA, user.id))
      .then(() => {
        this.clearBulkSelection();
        this.$data.bulkMode = false;
      })
      .catch((error: any) => {
        this.$data.bulkError = error && error.message ? error.message : "Bulk delete failed.";
      })
      .finally(() => {
        this.$data.bulkDeleting = false;
      });
  }

  exportPdf() {
    new PdfGenerator()
      .simpleTable("Events", this.exportHeaders(), this.exportRows(this.sortedFilteredEvents), "A3", "landscape")
      .then((pdfDoc: any) => {
        pdfDoc.download("event-list.pdf");
      });
  }

  exportExcel() {
    const data = [this.exportHeaders()].concat(this.exportRows(this.sortedFilteredEvents));
    generateSpreadsheetSingleTableAndDownload(data, "event-list.xlsx");
  }

  exportHeaders() {
    return ["Start", "End", "Facility", "Facility Contact", "Subject", "Attendees", "Detail"];
  }

  exportRows(events: CalendarEntry[]) {
    return events.map(event => [
      this.formatDate(event.start),
      this.formatDate(event.end),
      this.facilityName(event.facility),
      this.contactName(this.getContactForFacility(event.facility)),
      this.displayValue(event.subject),
      this.attendeeText(event),
      this.displayValue(event.detail)
    ]);
  }

  formatDate(value: number | Date | null | undefined) {
    if (value === undefined || value === null) {
      return "";
    }
    return dateFormatter()(value);
  }

  facilityName(value: number | undefined | null) {
    const facility = this.getFacilityForId(value);
    return facility === undefined ? "None" : this.displayValue(facility.name);
  }

  contactName(contact: Contact | undefined) {
    if (contact === undefined) {
      return "None";
    }
    return [contact.firstName, contact.lastName].filter(value => this.displayValue(value) !== "").join(" ") || "None";
  }

  attendeeText(event: CalendarEntry) {
    const names = this.attendeesFor(event).map(attendee => this.getAttendee(attendee).name).filter(name => name);
    return names.length === 0 ? "None" : names.join(", ");
  }

  searchableText(event: CalendarEntry) {
    return this.normalize([
      this.formatDate(event.start),
      this.formatDate(event.end),
      this.facilityName(event.facility),
      this.contactName(this.getContactForFacility(event.facility)),
      this.attendeeText(event),
      event.subject,
      event.detail
    ].join(" "));
  }

  displayValue(value: unknown) {
    return value === undefined || value === null ? "" : `${value}`;
  }

  normalize(value: unknown) {
    return this.displayValue(value).toLowerCase();
  }

  valueForSort(item: CalendarEntry, key: string) {
    if (key === "start" || key === "end") {
      const value = item[key];
      return value === undefined || value === null ? "" : `${value}`;
    }
    if (key === "facility") {
      return this.facilityName(item.facility);
    }
    if (key === "facility_contact") {
      return this.contactName(this.getContactForFacility(item.facility));
    }
    return this.displayValue((item as any)[key]);
  }

  sortCompare(firstItem: CalendarEntry, secondItem: CalendarEntry, key: string) {
    return this.valueForSort(firstItem, key).localeCompare(
      this.valueForSort(secondItem, key),
      undefined,
      { numeric: true, sensitivity: "base" }
    );
  }
}
